import appData from '../../package.json';
import { useOnlineStatus } from "../hooks/useOnlineStatus.js";
import { useStores } from "../hooks/useStores.js";
import { useCatalogs } from "../hooks/useCatalogs.js";
import { useCategories } from "../hooks/useCategories.js";
import { useBanners } from "../hooks/useBanners.js";
import { useItems } from "../hooks/useItems.js";
import { useModifiers } from "../hooks/useModifiers.js";
import { useTheme } from "../hooks/useTheme.jsx";
import { useDebug } from "../hooks/useDebug.js";
import { useAppReleases } from "../hooks/useAppReleases.js";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog.jsx";
import { useState, useEffect, useRef } from "react";
import { LoaderCircle } from 'lucide-react';
import { Printer } from '../utils/Printer.js';
import { pairTerminal } from '../utils/posPolling.js';
import { motion, AnimatePresence } from 'motion/react';
import { SVGWrapper } from './SVGWrapper.jsx';
import { Input } from './ui/input';
import userIcon from '../images/user.svg';
import cogIcon from '../images/cog.svg';
import { PRINTER_APPS, PRINTER_APP_IDS, getCurrentPlatform, cleanNotes, formatSize, downloadPrinterApp } from '../utils/printerApps.js';

export function SecretMenu({ clientId, storeId, instance, reloadApp, onResetConfig, onClose, pollingViaAPI, isOpen = false, isConnectingPOS, setIsConnectingPOS, logActivity, onGoToCashClosing, style, drawerContainer }) {

  const env = import.meta.env.REACT_APP_ENV || "development";
  const paymentMethods = style?.global?.paymentMethods || [];
  const {stores} = useStores(env, clientId);
  const {catalogs} = useCatalogs(env, clientId);
  const {categories} = useCategories(env, clientId);
  const {banners} = useBanners(env, clientId);
  const {items} = useItems(env, clientId);
  const {modifiers} = useModifiers(env, clientId);

  const primaryColor = style?.global?.primary_color;
  const online = useOnlineStatus();
  const serialNumberInput = useRef(null);
  const { theme, setTheme } = useTheme();

  // SOK-04: antes la app se recargaba al perder la red. Eso sacaba al cliente del catálogo y cortaba
  // la pantalla de un cobro en curso. Ahora el tótem sigue con la caché y solo bloquea cobros nuevos.
  
  const { debug, setDebug } = useDebug(false);
  const [isReloadingSW, setIsReloadingSW] = useState(false);
  const [isRecachingImages, setIsRecachingImages] = useState(false);
  const [requestedPOSCheck, setRequestedPOSCheck] = useState(false);
  const [hasPOSConnection, setHasPOSConnection] = useState(false);
  const [verifyingPOSConnection, setVerifyingPOSConnection] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [posConnectionType, setPOSConnectionType] = useState(null);
  const [serialNumber, setSerialNumber] = useState('');
  const [isAttemptingConnection, setIsAttemptingConnection] = useState(false);
  const [connectionMessage, setConnectionMessage] = useState(null);
  const [connectionMessageType, setConnectionMessageType] = useState(null);
  const pollingAbortController = useRef(null);
  const [isViewingPrinterApps, setIsViewingPrinterApps] = useState(false);
  const {loadedReleases, releases} = useAppReleases(PRINTER_APP_IDS, isOpen && isViewingPrinterApps);
  const currentPlatform = getCurrentPlatform();

  useEffect(() => {
    if (!isOpen) {
      if (pollingAbortController.current) {
        pollingAbortController.current.abort();
        pollingAbortController.current = null;
      }
      setVerifyingPOSConnection(false);
      setIsViewingPrinterApps(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (primaryColor) document.documentElement.style.setProperty("--client-primary", "#00FF00");
  }, [primaryColor]);

  const handleClose = () => {
    logActivity('secret-menu', 'close-button/overlay', 'click', true, null);
    handleCloseConnectionMenu(false);
    setIsViewingPrinterApps(false);
    setShowPanel(false);
    onClose();
  };

  const handleCloseConnectionMenu = (sendMetric) => {
    if (sendMetric) logActivity('secret-menu', 'connect-pos', 'close', true, null);
    setIsConnectingPOS(false);
    setPOSConnectionType(null);
    setShowPanel(true);
    setSerialNumber('');
  };

  const handleChangeConfig = async () => {
    logActivity('secret-menu', 'change-config', 'click', true, null);
    if (onClose) onClose();
    // La limpieza de imágenes corre en paralelo con el cierre de sesión en lugar de antes.
    if (onResetConfig) onResetConfig(clearImageCache());
    else await clearImageCache();
  };

  const handleCashClosingView = () => {
    logActivity('secret-menu', 'open-cash-closing', 'click', true, null);
    if (onGoToCashClosing) onGoToCashClosing();
  };

  const handleThemeChange = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    logActivity('secret-menu', 'change-theme', 'click', true, {new_theme: newTheme});
    setTheme(newTheme);
  };

  const handleDebugToggle = () => {
    const newValue = debug === false ? true : false;
    logActivity('secret-menu', 'toggle-debug', 'click', true, {debug: newValue});
    setDebug(newValue);
  };

  const handleReloadServiceWorker = async () => {
    setIsReloadingSW(true);
    logActivity('secret-menu', 'reload-app', 'click', true, null);
    if (reloadApp) reloadApp();
  };

  const clearImageCache = async () => {
    const RUNTIME_CACHE_NAME = 'self-ordering-kiosk-runtime-v1';
    if ('caches' in window) {
      const cache = await caches.open(RUNTIME_CACHE_NAME);
      const keys = await cache.keys();
      const imageKeys = keys.filter((request) => {
        const url = new URL(request.url);
        return url.pathname.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || request.url.includes('firebasestorage.googleapis.com') || request.url.includes('storage.googleapis.com');
      });
      await Promise.all(imageKeys.map(key => cache.delete(key)));
    }
  };

  const handleRecacheImages = async () => {
    setIsRecachingImages(true);
    logActivity('secret-menu', 'recache-images', 'click', true, null);
    try {
      await clearImageCache();
      if (items && storeId) {
        const urls = new Set();
        items.forEach(item => {
          if (item.max_per_item <= 0) return;
          const storeInfo = item?.info_stores?.[storeId];
          if (!storeInfo || storeInfo.is_available !== true || storeInfo.max_per_item <= 0) return;
          if (item?.multimedia?.main_image?.thumbnail) urls.add(item?.multimedia?.main_image?.thumbnail);
          if (Array.isArray(item.variants)) {
            item.variants.forEach(variant => {
              if (variant?.multimedia?.main_image?.thumbnail) urls.add(variant?.multimedia?.main_image?.thumbnail);
            });
          }
        });
        const fetchPromises = Array.from(urls).map(async (url) => {
          try {
            const response = await fetch(url, { cache: 'reload' });
            if (response.ok) {
              return true;
            }
          } catch (error) {
            console.warn(`[SecretMenu] Failed to fetch image: ${url}`, error);
            const img = new Image();
            img.src = url;
          }
          return false;
        });
        await Promise.all(fetchPromises);
      } else {
        console.warn('No item found for caching');
      }
      setTimeout(() => {setIsRecachingImages(false);}, 1000);
    } catch (error) {
      console.error('[SecretMenu] Error re-caching images:', error);
      setTimeout(() => {setIsRecachingImages(false);}, 1000);
    }
  };

  const getPollingViaAPI = async (type) => {
    setHasPOSConnection(false);
    setRequestedPOSCheck(true);
    setVerifyingPOSConnection(true);
    pollingAbortController.current = new AbortController();
    const signal = pollingAbortController.current.signal;
    const response = await pollingViaAPI(signal, type);
    pollingAbortController.current = null;
    const hasConnection = response.status === 'OK';
    logActivity('secret-menu', 'verify-pos-connection', 'click', true, {type: type, has_connection: hasConnection});
    setHasPOSConnection(hasConnection);
    setVerifyingPOSConnection(false);
  };

  const handleSetPrinterLink = () => {
    logActivity('secret-menu', 'printer-link-attempt', 'click', true, null);
    Printer.sendLinkIntent(clientId, instance.id, env);
  };

  const handleOpenPrinterApps = () => {
    logActivity('secret-menu', 'printer-apps', 'click', true, {platform: currentPlatform});
    setIsViewingPrinterApps(true);
  };

  const handleClosePrinterApps = () => {
    logActivity('secret-menu', 'printer-apps', 'close', true, null);
    setIsViewingPrinterApps(false);
  };

  const handleDownloadPrinterApp = (app, stable) => {
    logActivity('secret-menu', 'printer-apps', 'download', true, {app: app.id, platform: app.platform, version: stable.version, current_platform: currentPlatform});
    downloadPrinterApp(stable);
  };

  const handleDetailsToggle = (event) => {
    const open = event.target.open;
    logActivity('secret-menu', 'toggle-details', 'click', true, {is_open: open});
  };

  const handleCloseApp = () => {
    logActivity('secret-menu', 'close-app', 'click', true, null);
    try {
      window.close();
    } catch (error) {
      console.error('Error closing window:', error);
    }
  };

  useEffect(() => {
    if (isConnectingPOS) serialNumberInput.current?.focus();
  }, [isConnectingPOS]);

  const connectPOS = async () => {
    if (!env || !clientId || !instance?.id || !serialNumber) return;
    setIsAttemptingConnection(true);
    setConnectionMessage(null);
    setConnectionMessageType(null);
    const result = await pairTerminal({ env, client: clientId, instanceId: instance.id, serialNumber });
    if (result.ok) {
      setConnectionMessage('Conexión establecida exitosamente');
      setConnectionMessageType('success');
    } else {
      setConnectionMessage(result.message);
      setConnectionMessageType('error');
    }
    setIsAttemptingConnection(false);
    logActivity('secret-menu', 'connect-pos', 'connect', true, {status: result.ok ? 'success' : 'error', serial_number: serialNumber});
  };

  const mainMenu = (
    <div className="w-full h-auto overflow-y-auto" style={{maxHeight: '70vh', padding: '0px 32px 40px 32px'}}>
      <div className="secondary-font-regular text-xl mt-0 mb-4 line-clamp-2 text-muted flex items-center justify-start">
        <div className="w-[20px] h-[20px] mr-2">
          <SVGWrapper
            image={userIcon}
            alt="Acceso General"
            display="block"
            mode="contain"
            width="100%"
            height="100%"
            backgroundColor="transparent"
            margin="0"
            padding="0"
            borderRadius="0"
            transition={true}
            loadDelay={0.1}
            onLoad={undefined}
          />
        </div>
        Acceso general
      </div>
      <motion.button
        onClick={handleCashClosingView}
        className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
        whileTap={{ scale: 0.9 }}
        whileHover={{ scale: 1.05 }}
      >
        Ver Cierres de Caja
      </motion.button>
      <motion.button
        onClick={handleReloadServiceWorker}
        disabled={isReloadingSW}
        className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
        whileTap={{ scale: 0.9 }}
        whileHover={{ scale: 1.05 }}
      >
        {isReloadingSW ? (
          <div className='spin mr-1'>
            <LoaderCircle width={30} height={30}/>
          </div>
        ) : null}
        {isReloadingSW ? "Recargando..." : "Recargar aplicación"}
      </motion.button>
      <motion.button
        onClick={handleSetPrinterLink}
        disabled={false}
        className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
        whileTap={{ scale: 0.9 }}
        whileHover={{ scale: 1.05 }}
      >
        Conectar impresora
      </motion.button>
      <div className="border-t border-muted my-4"></div>
      <motion.button
        onClick={() => {
          logActivity('secret-menu', 'advanced-config', 'toggle', true, {is_open: !showPanel});
          setShowPanel(prev => !prev);
        }}
        disabled={false}
        className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2 text-muted border-4!"
        style={{background: '#FFFFFF', borderColor: '#f9ce7d'}}
        whileTap={{ scale: 0.9 }}
        whileHover={{ scale: 1.05 }}
      >
        <div className="w-[20px] h-[20px] mr-2">
          <SVGWrapper
            image={cogIcon}
            alt="Acceso General"
            display="block"
            mode="contain"
            width="100%"
            height="100%"
            backgroundColor="transparent"
            margin="0"
            padding="0"
            borderRadius="0"
            transition={true}
            loadDelay={0.1}
            onLoad={undefined}
          />
        </div>
        Configuración Avanzada
      </motion.button>
      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <motion.button
              onClick={handleChangeConfig}
              className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.05 }}
            >
              Cambiar Configuración
            </motion.button>
            <motion.button
              onClick={handleThemeChange}
              className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.05 }}
            >
              Cambiar tema (actual: {theme})
            </motion.button>
            <motion.button
              onClick={handleDebugToggle}
              className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.05 }}
            >
              Activar debug (actual: {debug ? "Sí" : "No"})
            </motion.button>
            <motion.button
              onClick={handleRecacheImages}
              disabled={isRecachingImages}
              className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.05 }}
            >
              {isRecachingImages ? (
                <div className='spin mr-1'>
                  <LoaderCircle width={30} height={30}/>
                </div>
              ) : null}
              {isRecachingImages ? "Re-cacheando..." : "Re-cachear Imágenes"}
            </motion.button>
            <motion.button
              onClick={handleOpenPrinterApps}
              disabled={false}
              className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.05 }}
            >
              Apps de impresión
            </motion.button>
            {paymentMethods.filter((pm) => pm.type === 'POSAPI').map((paymentMethod) => (
              <motion.button
                key={`connect-pos-${paymentMethod.type_details}`}
                onClick={() => {
                  logActivity('secret-menu', 'connect-pos', 'click', true, {type: paymentMethod.type_details});
                  setIsConnectingPOS(true);
                  setPOSConnectionType(paymentMethod.type_details);
                }}
                disabled={false}
                className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
                whileTap={{ scale: 0.9 }}
                whileHover={{ scale: 1.05 }}
              >
                Conectar POS ({paymentMethod.type_details})
              </motion.button>
            ))}
            {paymentMethods.filter((pm) => pm.type !== 'CASH').map((paymentMethod) => {
              return (
                <motion.button
                  key={`payment-method-${paymentMethod.type_details}`}
                  onClick={() => getPollingViaAPI(paymentMethod.type_details)}
                  disabled={verifyingPOSConnection}
                  className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
                  whileTap={{ scale: 0.9 }}
                  whileHover={{ scale: 1.05 }}
                >
                  {verifyingPOSConnection ? (
                    <div className='spin mr-1'>
                      <LoaderCircle width={30} height={30}/>
                    </div>
                  ) : null}
                  {verifyingPOSConnection ? "Verificando conexión..." : `Poll POS (${paymentMethod.type_details})`}
                </motion.button>
              );
            })}
            <motion.button
              onClick={handleCloseApp}
              disabled={false}
              className="cursor-pointer btn w-full text-2xl! main-font-regular capitalize my-2"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.05 }}
              style={{background: '#7a1b19', color: '#FFFFFF', borderColor: '#7a1b19'}}
            >
              Cerrar aplicación
            </motion.button>
            <div className="border-t border-muted my-4"></div>
            <div className="flex items-center justify-center w-full">
              <p className="w-full text-center text-lg main-font-regular my-2">
                POS: <span className="font-bold">{!verifyingPOSConnection && requestedPOSCheck ? (hasPOSConnection ? "  🟢 Online" : "  🔴 Offline") : 'Por Probar'}</span>
              </p>
              <p className="w-full text-center text-lg main-font-regular my-2">
                RED: <span className="font-bold">{online ? "🟢 Online" : "🔴 Offline"}</span>
              </p>
            </div>
            <div className="border-t border-muted my-4"></div>
            <details onToggle={handleDetailsToggle}>
              <summary className="cursor-pointer main-font-regular w-full text-left text-lg">Datos de conexión</summary>
              <pre className="w-full" style={{ background: "#222", color: "#0f0", padding: "10px", textAlign: "left" }}>
                <code className="w-full">
                  {JSON.stringify({
                    "total_stores": stores ? stores.length : 0,
                    "total_catalogs": catalogs ? catalogs.length : 0,
                    "total_categories": categories ? categories.length : 0,
                    "total_banners": banners ? banners.length : 0,
                    "total_items": items ? items.length : 0,
                    "total_modifiers": modifiers ? modifiers.length : 0,
                    "client": clientId,
                    "instance": instance,
                    "version": appData.version,
                  }, null, 2)}
                </code>
              </pre>
            </details>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  const connectionMenu = (
    <div className="w-full px-12">
      <Input
        ref={serialNumberInput}
        id="search-input"
        type="text"
        placeholder="Ingrese el número de serie"
        value={serialNumber}
        onChange={(e) => {
          setSerialNumber(e.target.value);
        }}
        className="h-[64px] w-full rounded-[16px] border-3 border-[#d1d5dc] px-[24px] main-font-bold text-2xl placeholder:text-[#99a1af] tracking-[-0.4395px] focus:border-[#DE2D7A] focus:ring-0 mb-6"
      />
      {connectionMessage && (
        <p className={`secondary-font-regular text-xl mt-0 mb-4 line-clamp-2 text-center w-full ${connectionMessageType === 'error' ? 'text-red-500' : ''}`}>
          {connectionMessage}
        </p>
      )}
      <div className="space-y-3 py-4">
        <motion.button
          type="button"
          onClick={connectPOS}
          disabled={isAttemptingConnection || !serialNumber}
          className="w-full btn btn-primary text-center text-[#FAFAFA] cursor-pointer"
          style={{height: '64px', fontSize: '18px', lineHeight: '27px'}}
          whileTap={{ scale: 0.98 }}
          animate={!serialNumber ? {} : {boxShadow: ['0px 0px 30px 0px var(--color-primary-glow)', '0px 0px 30px 15px var(--color-primary-glow)', '0px 0px 30px 0px var(--color-primary-glow)']}}
          transition={{delay: 0, duration: 2, repeat: Infinity, ease: 'linear'}}
        >
          {isAttemptingConnection ? (
            <div className='spin mr-1'>
              <LoaderCircle width={30} height={30}/>
            </div>
          ) : null}
          <span className='text-3xl'>
            {isAttemptingConnection ? 'Conectando...' : 'Conectar'}
          </span>
        </motion.button>
        <button
          type="button"
          onClick={() => handleCloseConnectionMenu(true)}
          disabled={false}
          className="w-full text-center secondary-font-bold cursor-pointer"
          style={{color: '#6a7282', fontSize: '16px', lineHeight: '24px', padding: '12px 0'}}>
            Cancelar
          </button>
      </div>
    </div>
  );

  const printerAppsMenu = (
    <div className="w-full px-12">
      <div className="overflow-y-auto" style={{maxHeight: '60vh'}}>
        {!loadedReleases ? (
          <div className="flex items-center justify-center py-8">
            <div className='spin'>
              <LoaderCircle width={40} height={40}/>
            </div>
          </div>
        ) : PRINTER_APPS.map((app) => {
          const stable = releases[app.id]?.stable;
          const notes = cleanNotes(stable?.notes);
          return (
            <div
              key={app.id}
              className="w-full rounded-[16px] border-3 border-[#d1d5dc] p-6 mb-4"
            >
              <p className="main-font-bold text-2xl mb-2">{app.label}</p>
              {stable?.url ? (
                <>
                  <p className="secondary-font-regular text-lg">
                    Versión <span className="font-bold">{stable.version}</span>
                    {formatSize(stable.size) ? ` · ${formatSize(stable.size)}` : ''}
                  </p>
                  {notes && (
                    <p className="secondary-font-regular text-base text-muted mt-2 whitespace-pre-line">{notes}</p>
                  )}
                  <motion.button
                    type="button"
                    onClick={() => handleDownloadPrinterApp(app, stable)}
                    className="w-full btn btn-primary text-center text-[#FAFAFA] text-2xl! main-font-regular cursor-pointer mt-4"
                    whileTap={{ scale: 0.98 }}
                  >
                    Descargar
                  </motion.button>
                </>
              ) : (
                <p className="secondary-font-regular text-lg text-muted">No hay versión publicada</p>
              )}
            </div>
          );
        })}
      </div>
      <div className="py-4">
        <button
          type="button"
          onClick={handleClosePrinterApps}
          disabled={false}
          className="w-full text-center secondary-font-bold cursor-pointer"
          style={{color: '#6a7282', fontSize: '16px', lineHeight: '24px', padding: '12px 0'}}>
            Volver
          </button>
      </div>
    </div>
  );

  if (isOpen) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose && handleClose()}>
        <DialogContent
          aria-describedby="Secret Menu"
          description="Secret Menu"
          container={drawerContainer}
          className="bg-back shadow-2xl border-[4px] max-w-[800px] w-full max-h-[90vh] overflow-hidden sm:max-w-lg rounded-lg"
          style={{borderRadius: '24px', padding: 0, boxSizing: 'border-box'}}
        >
          <div className="w-full!" style={{maxHeight: '1000px'}}>
            {isConnectingPOS ? (
                <>
                  <DialogTitle className="text-3xl text-center main-font-bold p-8">Conectar POS ({posConnectionType})</DialogTitle>
                  {connectionMenu}
                </>
              ) : isViewingPrinterApps ? (
                <>
                  <DialogTitle className="text-3xl text-center main-font-bold p-8">Apps de impresión</DialogTitle>
                  {printerAppsMenu}
                </>
              ) : (
                <>
                  <DialogTitle className="text-3xl text-center main-font-bold p-8">Opciones</DialogTitle>
                  {mainMenu}
                </>
              )}
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  return null;
}